import { COLORS, type Color, type Config, type Effect } from '@ml/config';
import { compareRowMajor, indexOf } from '../board/grid.ts';
import type { Board, BoosterKind, Cell, Coord, Overlay } from '../board/types.ts';
import type { Rng } from '../rng.ts';
import { essenceRadius } from './damage.ts';

export type FxConfig = Pick<Config, 'booster' | 'essence' | 'debuff' | 'board'>;

/** Một booster/light ball đang chờ kích hoạt (đã bị lấy khỏi bảng). */
export type Activator =
  | { readonly t: 'booster'; readonly kind: BoosterKind; readonly color: Color }
  | { readonly t: 'lightball'; readonly color: Color | null };

export type QueueItem =
  | { readonly k: 'single'; readonly act: Activator; readonly at: Coord }
  | { readonly k: 'combo'; readonly a: Activator; readonly b: Activator; readonly at: Coord }
  | { readonly k: 'essence'; readonly charge: number; readonly at: Coord };

export type HitResult =
  | 'empty'
  | 'shielded'
  | 'destroyed'
  | 'layerRemoved'
  | 'unlocked'
  | 'defused'
  | 'booster'
  | 'essence';

export type FxEvent =
  | { readonly type: 'hit'; readonly at: Coord; readonly result: HitResult }
  | {
      readonly type: 'activate';
      readonly at: Coord;
      readonly source: BoosterKind | 'lightball' | 'combo';
      readonly parts?: readonly (BoosterKind | 'lightball')[];
      readonly color?: Color | null;
    }
  | { readonly type: 'propellerTarget'; readonly from: Coord; readonly to: Coord }
  | { readonly type: 'convert'; readonly at: Coord; readonly cell: Cell }
  | {
      readonly type: 'detonate';
      readonly at: Coord;
      readonly charge: number;
      readonly radius: number;
    }
  | { readonly type: 'cleanse'; readonly at: Coord; readonly overlay: Overlay['type'] }
  | { readonly type: 'debuff'; readonly at: Coord; readonly effect: Effect['type'] };

/** Lặp `count` lần: `rng.nextInt(n)` trên danh sách còn lại, bỏ ô đã chọn (orbs.md 7). */
export function pickDistinct<T>(rng: Rng, list: readonly T[], count: number): T[] {
  const pool = list.slice();
  const out: T[] = [];
  while (out.length < count && pool.length > 0) {
    const i = rng.nextInt(pool.length);
    out.push(pool.splice(i, 1)[0]!);
  }
  return out;
}

const rocketLike = (a: Activator): a is Activator & { t: 'booster' } =>
  a.t === 'booster' && (a.kind === 'rocketH' || a.kind === 'rocketV');
const kindOf = (a: Activator): BoosterKind | 'lightball' =>
  a.t === 'lightball' ? 'lightball' : a.kind;

/**
 * Bộ máy hiệu ứng lên bảng trong một lần resolve (orbs.md 4.2–4.3, 6.1 R3–R6, 6.3). Giữ bảng
 * dạng mảng đổi được, tập ô được che chắn, hàng đợi kích hoạt FIFO, số collateral và các lần
 * ngọc ấn nổ. Không làm gravity (R8 là việc của resolve).
 */
export class BoardFx {
  readonly width: number;
  readonly height: number;
  readonly cells: (Cell | null)[];
  readonly shielded = new Set<number>();
  /** Ô đã bị một propeller nhắm trong lần resolve này. */
  readonly targeted = new Set<number>();
  readonly queue: QueueItem[] = [];
  readonly events: FxEvent[] = [];
  readonly detonations: { readonly at: Coord; readonly charge: number }[] = [];
  collateral = 0;

  constructor(
    board: Board,
    readonly rng: Rng,
    readonly config: FxConfig,
  ) {
    this.width = board.width;
    this.height = board.height;
    this.cells = board.cells.slice();
  }

  board(): Board {
    return { width: this.width, height: this.height, cells: this.cells.slice() };
  }

  idx(c: Coord): number {
    return indexOf(this, c.x, c.y);
  }
  get(c: Coord): Cell | null {
    return this.cells[this.idx(c)] ?? null;
  }
  set(c: Coord, cell: Cell | null): void {
    this.cells[this.idx(c)] = cell;
  }
  inBounds(c: Coord): boolean {
    return c.x >= 0 && c.y >= 0 && c.x < this.width && c.y < this.height;
  }
  /** Mọi ô theo thứ tự hàng thoả `pred`. */
  coords(pred: (cell: Cell, c: Coord) => boolean): Coord[] {
    const out: Coord[] = [];
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        const cell = this.cells[y * this.width + x];
        if (cell && pred(cell, { x, y })) out.push({ x, y });
      }
    }
    return out;
  }

  // ---- vùng (luôn trả theo thứ tự hàng, không trùng, không gồm ô tâm nếu ghi rõ) ----
  rows(y0: number, y1: number): Coord[] {
    const out: Coord[] = [];
    for (let y = Math.max(0, y0); y <= Math.min(this.height - 1, y1); y++)
      for (let x = 0; x < this.width; x++) out.push({ x, y });
    return out;
  }
  cols(x0: number, x1: number): Coord[] {
    const out: Coord[] = [];
    for (let y = 0; y < this.height; y++)
      for (let x = Math.max(0, x0); x <= Math.min(this.width - 1, x1); x++) out.push({ x, y });
    return out;
  }
  square(c: Coord, r: number): Coord[] {
    const out: Coord[] = [];
    for (let y = c.y - r; y <= c.y + r; y++)
      for (let x = c.x - r; x <= c.x + r; x++) if (this.inBounds({ x, y })) out.push({ x, y });
    return out;
  }
  /** Chữ thập bán kính r quanh c, không gồm c. */
  cross(c: Coord, r: number): Coord[] {
    return this.square(c, r).filter(
      (p) => (p.x === c.x) !== (p.y === c.y) && Math.abs(p.x - c.x) + Math.abs(p.y - c.y) <= r,
    );
  }

  /** Gộp vùng, bỏ trùng, sắp theo thứ tự hàng, rồi đánh 1 hit lên từng ô. */
  hitAll(...areas: Coord[][]): void {
    const seen = new Set<number>();
    const list: Coord[] = [];
    for (const a of areas)
      for (const c of a) {
        const i = this.idx(c);
        if (!seen.has(i)) {
          seen.add(i);
          list.push(c);
        }
      }
    list.sort(compareRowMajor);
    for (const c of list) this.hit(c);
  }

  /** Một hit (orbs.md 6.3). */
  hit(c: Coord): HitResult {
    const i = this.idx(c);
    const cell = this.cells[i] ?? null;
    let result: HitResult;
    if (!cell) return 'empty';
    if (this.shielded.has(i)) result = 'shielded';
    else if (cell.t === 'orb') {
      const o = cell.overlay;
      if (o?.type === 'freeze') {
        const layers = o.layers - 1;
        this.cells[i] =
          layers > 0
            ? { ...cell, overlay: { type: 'freeze', layers } }
            : { t: 'orb', kind: cell.kind, color: cell.color };
        result = 'layerRemoved';
      } else if (o?.type === 'lock') {
        this.cells[i] = { t: 'orb', kind: cell.kind, color: cell.color };
        result = 'unlocked';
      } else {
        this.cells[i] = null;
        this.collateral++;
        result = o?.type === 'deadMark' ? 'defused' : 'destroyed';
      }
    } else if (cell.t === 'essence') {
      this.cells[i] = null;
      this.queue.push({ k: 'essence', charge: cell.charge, at: c });
      result = 'essence';
    } else {
      this.cells[i] = null;
      this.collateral++;
      this.queue.push({ k: 'single', act: activatorOf(cell), at: c });
      result = 'booster';
    }
    this.events.push({ type: 'hit', at: c, result });
    return result;
  }

  /** R6: xử lý hàng đợi FIFO tới khi rỗng. */
  runQueue(): void {
    for (let item = this.queue.shift(); item; item = this.queue.shift()) this.process(item);
  }

  process(item: QueueItem): void {
    if (item.k === 'essence') this.detonate(item.charge, item.at);
    else if (item.k === 'combo') this.combo(item.a, item.b, item.at);
    else this.activate(item.act, item.at);
  }

  detonate(charge: number, at: Coord): void {
    const radius = essenceRadius(charge, this.config);
    this.detonations.push({ at, charge });
    this.events.push({ type: 'detonate', at, charge, radius });
    this.hitAll(this.square(at, radius));
  }

  /** Màu light ball khi chạm riêng / bị hit (orbs.md 4.2, `tapColorRule`). */
  lightballColor(): Color | null {
    const counts = COLORS.map(
      (col) =>
        this.coords((cell) => (cell.t === 'orb' || cell.t === 'booster') && cell.color === col)
          .length,
    );
    const present = COLORS.filter((_, k) => counts[k]! > 0);
    if (present.length === 0) return null;
    if (this.config.booster.lightball.tapColorRule === 'random') {
      return present[this.rng.nextInt(present.length)]!;
    }
    let best = 0;
    for (let k = 1; k < COLORS.length; k++) if (counts[k]! > counts[best]!) best = k;
    return COLORS[best]!;
  }

  /** Kích hoạt một booster/light ball đơn (orbs.md 4.2). */
  activate(act: Activator, at: Coord): void {
    if (act.t === 'lightball') {
      const color = act.color ?? this.lightballColor();
      this.events.push({ type: 'activate', at, source: 'lightball', color });
      if (color !== null) {
        this.hitAll(
          this.coords((cell) => (cell.t === 'orb' || cell.t === 'booster') && cell.color === color),
        );
      }
      return;
    }
    this.events.push({ type: 'activate', at, source: act.kind, color: act.color });
    this.payload(act.kind, at);
  }

  /** Hiệu ứng vùng của một loại booster tại `at` (propeller: chữ thập + bay 1 mục tiêu). */
  private payload(kind: BoosterKind, at: Coord): void {
    const b = this.config.booster;
    switch (kind) {
      case 'rocketH':
        return this.hitAll(this.rows(at.y, at.y));
      case 'rocketV':
        return this.hitAll(this.cols(at.x, at.x));
      case 'bomb':
        return this.hitAll(this.square(at, b.bomb.radius));
      case 'propeller': {
        const cross = this.cross(at, b.propeller.plusRadius);
        this.hitAll(cross);
        const t = this.propellerTarget(at, cross);
        if (t) this.hit(t);
        return;
      }
    }
  }

  /**
   * Mục tiêu propeller (orbs.md 4.2): theo `targetPriority`, mức đầu tiên có ô hợp lệ. Ô hợp
   * lệ: có viên, không được che chắn, không nằm trong chữ thập của chính propeller đó, chưa bị
   * propeller khác nhắm. Mức `any` rút `rng.nextInt(n)`; danh sách rỗng thì không rút.
   */
  propellerTarget(from: Coord, cross: readonly Coord[]): Coord | null {
    const excluded = new Set(cross.map((c) => this.idx(c)));
    excluded.add(this.idx(from));
    const ok = (c: Coord) => {
      const i = this.idx(c);
      return !excluded.has(i) && !this.targeted.has(i) && !this.shielded.has(i);
    };
    for (const level of this.config.booster.propeller.targetPriority) {
      const list = this.coords((cell, c) => ok(c) && matchesLevel(level, cell));
      if (list.length === 0) continue;
      const to = level === 'any' ? list[this.rng.nextInt(list.length)]! : list[0]!;
      this.targeted.add(this.idx(to));
      this.events.push({ type: 'propellerTarget', from, to });
      return to;
    }
    return null;
  }

  /** Combo 2 booster (orbs.md 4.3), đặt tại `at` (ô của booster thứ hai). */
  combo(a: Activator, b: Activator, at: Coord): void {
    this.events.push({ type: 'activate', at, source: 'combo', parts: [kindOf(a), kindOf(b)] });
    const cfg = this.config.booster;
    if (a.t === 'lightball' && b.t === 'lightball') {
      return this.hitAll(this.rows(0, this.height - 1));
    }
    if (a.t === 'lightball' || b.t === 'lightball') {
      const other = (a.t === 'booster' ? a : b) as Activator & { t: 'booster' };
      return this.lightballCombo(other);
    }
    const pa = a.kind === 'propeller';
    const pb = b.kind === 'propeller';
    if (pa && pb) {
      const cross = this.cross(at, cfg.propeller.plusRadius);
      this.hitAll(cross);
      for (let k = 0; k < cfg.combo.propellerPropeller.count; k++) {
        const t = this.propellerTarget(at, cross);
        if (!t) break;
        this.hit(t);
      }
      return;
    }
    if (pa || pb) {
      const other = (pa ? b : a) as Activator & { t: 'booster' };
      const cross = this.cross(at, cfg.propeller.plusRadius);
      this.hitAll(cross);
      const t = this.propellerTarget(at, cross);
      if (t) this.payload(other.kind, t);
      return;
    }
    const ra = rocketLike(a);
    const rb = rocketLike(b);
    if (ra && rb) return this.hitAll(this.rows(at.y, at.y), this.cols(at.x, at.x));
    if (ra || rb) {
      const h = Math.floor(cfg.combo.rocketBomb.width / 2);
      return this.hitAll(this.rows(at.y - h, at.y + h), this.cols(at.x - h, at.x + h));
    }
    return this.hitAll(this.square(at, cfg.combo.bombBomb.radius));
  }

  /**
   * Light ball + booster X: mọi orb thường màu C (màu của X) không có lớp phủ biến thành X
   * (rocket: H, V xen kẽ theo thứ tự hàng, bắt đầu H), rồi lần lượt theo thứ tự hàng: viên
   * nào còn trên bảng thì bị lấy ra (collateral +1) và kích hoạt ngay. Viên bị booster trước
   * đó hit thì đã vào hàng đợi theo luật hit thường (không kích hoạt hai lần).
   */
  private lightballCombo(other: Activator & { t: 'booster' }): void {
    const color = other.color;
    const targets = this.coords(
      (cell) => cell.t === 'orb' && !cell.overlay && cell.color === color,
    );
    const rocket = other.kind === 'rocketH' || other.kind === 'rocketV';
    const made: { at: Coord; cell: Cell }[] = targets.map((at, k) => {
      const kind: BoosterKind = rocket ? (k % 2 === 0 ? 'rocketH' : 'rocketV') : other.kind;
      const cell: Cell = { t: 'booster', kind, color };
      this.set(at, cell);
      this.events.push({ type: 'convert', at, cell });
      return { at, cell };
    });
    for (const m of made) {
      if (this.get(m.at) !== m.cell) continue;
      this.set(m.at, null);
      this.collateral++;
      this.activate(activatorOf(m.cell), m.at);
    }
  }

  // ---- hiệu ứng skill lên bảng (combat.md 4.3, pha R5) ----

  /** Hit lên `count` ô có viên chọn bằng RNG, rồi đánh theo thứ tự hàng. */
  hitRandom(count: number): void {
    const picked = pickDistinct(
      this.rng,
      this.coords(() => true),
      count,
    );
    this.hitAll(picked);
  }

  convertColor(count: number, to: Color, from?: Color): void {
    const list = this.coords(
      (cell) =>
        cell.t === 'orb' &&
        !cell.overlay &&
        cell.color !== to &&
        (from === undefined || cell.color === from),
    );
    for (const at of pickDistinct(this.rng, list, count)) {
      const cell = this.get(at) as Cell & { t: 'orb' };
      const next: Cell = { ...cell, color: to };
      this.set(at, next);
      this.events.push({ type: 'convert', at, cell: next });
    }
  }

  /** Biến `count` orb thường không lớp phủ (RNG) thành booster cùng màu, được che chắn. */
  spawnBooster(type: BoosterKind | 'lightball', count: number): void {
    const list = this.coords((cell) => cell.t === 'orb' && !cell.overlay);
    for (const at of pickDistinct(this.rng, list, count)) {
      const orb = this.get(at) as Cell & { t: 'orb' };
      const next: Cell =
        type === 'lightball' ? { t: 'lightball' } : { t: 'booster', kind: type, color: orb.color };
      this.set(at, next);
      this.shielded.add(this.idx(at));
      this.events.push({ type: 'convert', at, cell: next });
    }
  }

  /** Gỡ lớp phủ `count` viên: đánh dấu tử > băng > khoá, trong mỗi mức theo thứ tự hàng. */
  cleanse(count: number): void {
    let left = count;
    for (const type of ['deadMark', 'freeze', 'lock'] as const) {
      for (const at of this.coords((cell) => cell.t === 'orb' && cell.overlay?.type === type)) {
        if (left <= 0) return;
        const cell = this.get(at) as Cell & { t: 'orb' };
        this.set(at, { t: 'orb', kind: cell.kind, color: cell.color });
        this.events.push({ type: 'cleanse', at, overlay: type });
        left--;
      }
    }
  }

  /** Hit lên ngọc ấn, charge lớn nhất trước (hoà theo thứ tự hàng). */
  detonateEssence(count: number | 'all'): void {
    const list = this.coords((cell) => cell.t === 'essence');
    const charge = (c: Coord) => (this.get(c) as Cell & { t: 'essence' }).charge;
    list.sort((p, q) => charge(q) - charge(p) || compareRowMajor(p, q));
    const n = count === 'all' ? list.length : Math.min(count, list.length);
    for (const at of list.slice(0, n)) this.hit(at);
  }

  /**
   * Debuff lên bảng (orbs.md 7, combat.md 5.2): chỉ kind trong `debuff.eligibleKinds`. Khoá và
   * đánh dấu tử chọn viên chưa có lớp phủ; băng chọn viên chưa phủ hoặc đang băng (cộng lớp,
   * trần `maxLayers`); phá ngọc chọn mọi viên hợp lệ. Trả các ô bị tác động (thứ tự chọn).
   */
  applyDebuff(
    effect: Extract<Effect, { type: 'lockOrbs' | 'freezeOrbs' | 'deadMark' | 'destroyOrbs' }>,
    deadMarkDamage = 0,
  ): Coord[] {
    const d = this.config.debuff;
    const eligible = (cell: Cell): cell is Cell & { t: 'orb' } =>
      cell.t === 'orb' && (d.eligibleKinds as readonly string[]).includes(cell.kind);
    const list = this.coords((cell) => {
      if (!eligible(cell)) return false;
      if (effect.type === 'destroyOrbs') return true;
      if (effect.type === 'freezeOrbs') return !cell.overlay || cell.overlay.type === 'freeze';
      return !cell.overlay;
    });
    const picked = pickDistinct(this.rng, list, effect.count);
    for (const at of picked) {
      const cell = this.get(at) as Cell & { t: 'orb' };
      const bare = { t: 'orb' as const, kind: cell.kind, color: cell.color };
      if (effect.type === 'destroyOrbs') this.set(at, null);
      else if (effect.type === 'lockOrbs') {
        const turns = effect.durationTurns ?? d.lock.defaultDurationTurns;
        this.set(at, { ...bare, overlay: { type: 'lock', turns } });
      } else if (effect.type === 'freezeOrbs') {
        const prev = cell.overlay?.type === 'freeze' ? cell.overlay.layers : 0;
        const layers = Math.min(d.freeze.maxLayers, prev + (effect.layers ?? 1));
        this.set(at, { ...bare, overlay: { type: 'freeze', layers } });
      } else {
        const turns = effect.turns ?? d.deadMark.defaultTurns;
        this.set(at, { ...bare, overlay: { type: 'deadMark', turns, damage: deadMarkDamage } });
      }
      this.events.push({ type: 'debuff', at, effect: effect.type });
    }
    return picked;
  }
}

export function activatorOf(cell: Cell): Activator {
  if (cell.t === 'lightball') return { t: 'lightball', color: null };
  if (cell.t === 'booster') return { t: 'booster', kind: cell.kind, color: cell.color };
  throw new TypeError(`không phải booster: ${cell.t}`);
}

function matchesLevel(
  level: Config['booster']['propeller']['targetPriority'][number],
  cell: Cell,
): boolean {
  switch (level) {
    case 'deadMark':
      return cell.t === 'orb' && cell.overlay?.type === 'deadMark';
    case 'frozen':
      return cell.t === 'orb' && cell.overlay?.type === 'freeze';
    case 'locked':
      return cell.t === 'orb' && cell.overlay?.type === 'lock';
    case 'essence':
      return cell.t === 'essence';
    case 'booster':
      return cell.t === 'booster' || cell.t === 'lightball';
    case 'any':
      return true;
  }
}

/**
 * Áp một debuff bảng của monster (lockOrbs/freezeOrbs/deadMark/destroyOrbs) lên bảng mới.
 * Không gravity/refill (pha battle làm sau `destroyOrbs`). `deadMarkDamage` tính lúc đánh dấu.
 */
export function applyBoardDebuff(
  board: Board,
  effect: Extract<Effect, { type: 'lockOrbs' | 'freezeOrbs' | 'deadMark' | 'destroyOrbs' }>,
  rng: Rng,
  config: FxConfig,
  deadMarkDamage = 0,
): { board: Board; coords: Coord[]; events: FxEvent[] } {
  const fx = new BoardFx(board, rng, config);
  const coords = fx.applyDebuff(effect, deadMarkDamage);
  return { board: fx.board(), coords, events: fx.events };
}
